import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DataScienceContext } from "../context/DataScienceContext";
import { useBackgroundCustomerBasicInfo, useBackgroundTreeSpecies } from "@hooks/useBackgroundData";
import { getMoistureAwareUnit } from "../graph/GraphArea";
import { getVariableById, getVariableDisplayLabel } from "../utils/variableMetadata";
import { getMoistureContentDisplayMode } from "@helper/moistureContentDisplay";

const formatCalibrationSpecies = (slug) =>
  slug
    ? slug
        .split("_")
        .map((part, i) => (i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join(" ")
    : null;

function SeriesChips() {
  const {
    filterState,
    dragItemLeft,
    dragItemRight,
    dragItemLeftId,
    dragItemRightId,
    showHideLeftData,
    showHideRightData,
    setHideLeftData,
    setHideRightData,
    activeInstallations,
  } = useContext(DataScienceContext);

  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const moistureMode = getMoistureContentDisplayMode(user);
  const { treeSpecies } = useBackgroundTreeSpecies();
  const { customerBasicInfo } = useBackgroundCustomerBasicInfo();
  const [showCalibInfo, setShowCalibInfo] = useState(false);
  const usesLegacyMoistureFormula = customerBasicInfo?.use_new_moisture_formula === false;
  const defaultCalibrationSlug = usesLegacyMoistureFormula ? "populus_nigra" : "acer_platanoides";

  const hasPulseSensors = useMemo(
    () => !activeInstallations.every((installation) => installation.sensor.pulse_sensor == null),
    [activeInstallations]
  );

  const calibrationMappings = useMemo(() => {
    if (!hasPulseSensors) return [];
    const rows = [];
    const seen = new Set();
    activeInstallations
      .filter((inst) => inst.sensor?.pulse_sensor != null)
      .forEach((inst) => {
        const tree = filterState.trees.find((tr) => String(tr.id) === String(inst.tree?.id));
        const treeName = tree?.name || inst.tree?.name || t("unknown");
        const speciesId = tree?.species_id ?? tree?.species?.id;
        const entry = speciesId
          ? treeSpecies.find((sp) => String(sp.id) === String(speciesId))
          : null;
        const speciesName =
          entry?.botanical_name ||
          entry?.species_name ||
          tree?.species_name ||
          tree?.species?.name ||
          t("calibration_species_unknown_tree_species");
        const deName = entry?.species_name_de || tree?.species?.de_name || null;
        const calibrationSlug = entry?.calibration_species || defaultCalibrationSlug;
        const calibName = formatCalibrationSpecies(calibrationSlug);
        const key = `${tree?.id ?? treeName}|${calibrationSlug}`;
        if (seen.has(key)) return;
        seen.add(key);
        rows.push({
          treeName,
          speciesName,
          deName,
          calibName,
          usesDefault: !entry?.calibration_species,
        });
      });
    return rows;
  }, [
    activeInstallations,
    defaultCalibrationSlug,
    filterState?.trees,
    hasPulseSensors,
    t,
    treeSpecies,
  ]);

  if (!dragItemLeft && !dragItemRight) return null;

  const getDisplayName = (dragItem, variableId) => {
    const def = variableId != null ? getVariableById(variableId) : null;
    return def ? getVariableDisplayLabel(def, t) : t(dragItem);
  };

  const getLabel = (dragItem, variableId) => {
    const displayName = getDisplayName(dragItem, variableId);
    const unit = getMoistureAwareUnit(dragItem, moistureMode);
    return unit ? `${displayName} (${unit})` : displayName;
  };

  const renderChip = (dragItem, side) => {
    if (!dragItem) return null;
    const isLeft = side === "left";
    const variableId = isLeft ? dragItemLeftId : dragItemRightId;
    const isHidden = isLeft ? showHideLeftData : showHideRightData;
    const toggle = () => (isLeft ? setHideLeftData(!isHidden) : setHideRightData(!isHidden));
    const unit = getMoistureAwareUnit(dragItem, moistureMode);
    const showCalib = dragItem === "moisture_content" && hasPulseSensors;
    const defaultBadge = usesLegacyMoistureFormula ? t("default_legacy") : t("default");
    const calibrationTitle = showCalib
      ? [
          t("calibration_species_tooltip"),
          ...calibrationMappings.map(
            ({ treeName, speciesName, deName, calibName, usesDefault }) =>
              `${treeName} | ${deName ?? speciesName}: ${calibName}${
                usesDefault ? ` (${defaultBadge})` : ""
              }`
          ),
        ].join("\n")
      : getLabel(dragItem, variableId);

    return (
      <div className="ds-series-dock__slot">
        <button
          className={`ds-series-chip${isHidden ? " ds-series-chip--hidden" : ""}`}
          onClick={toggle}
          aria-label={getLabel(dragItem, variableId)}
          aria-pressed={!isHidden}
          title={calibrationTitle}
        >
          <svg width="12" height="8" aria-hidden="true" className="ds-series-chip__line">
            <line
              x1="0"
              y1="4"
              x2="12"
              y2="4"
              stroke="currentColor"
              strokeWidth="2"
              {...(!isLeft ? { strokeDasharray: "3,2" } : {})}
            />
          </svg>
          <span className="ds-series-chip__label">
            {getDisplayName(dragItem, variableId)}
            {unit && <span className="ds-series-chip__unit"> ({unit})</span>}
          </span>
          {showCalib && (
            <span
              className="ds-series-chip__info"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setShowCalibInfo((v) => !v);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCalibInfo((v) => !v);
                }
              }}
              title={t("calibration_species_tooltip")}
              aria-label={t("calibration_species_tooltip")}
            >
              &#8505;
            </span>
          )}
        </button>
        {showCalib && showCalibInfo && (
          <div
            className="ds-series-dock__calib-popup"
            style={{ maxHeight: "320px", overflowY: "auto", overflowX: "hidden" }}
          >
            <div className="ds-series-dock__calib-header">
              <span style={{ flex: 1 }}>{t("tree_name")}</span>
              <span style={{ flex: 1 }}>{t("tree_species")}</span>
              <span style={{ flex: 1, textAlign: "right" }}>{t("calibration_species")}</span>
            </div>
            {calibrationMappings.map(
              ({ treeName, speciesName, deName, calibName, usesDefault }) => (
                <div
                  key={`${treeName}-${speciesName}-${calibName}`}
                  className="ds-series-dock__calib-row"
                >
                  <span style={{ flex: 1 }}>{treeName}</span>
                  <span style={{ flex: 1 }}>
                    {deName ?? speciesName}
                    {deName && <span className="ds-series-dock__calib-latin">{speciesName}</span>}
                  </span>
                  <span className="ds-series-dock__calib-value">
                    {calibName ?? "—"}
                    {usesDefault ? ` (${defaultBadge})` : ""}
                  </span>
                </div>
              )
            )}
            {calibrationMappings.length === 0 && (
              <div className="ds-series-dock__calib-row">
                <span style={{ flex: 1 }}>{t("unknown")}</span>
                <span style={{ flex: 1 }}>{t("calibration_species_no_mapping")}</span>
                <span className="ds-series-dock__calib-value">
                  {formatCalibrationSpecies(defaultCalibrationSlug)} ({defaultBadge})
                </span>
              </div>
            )}
            {usesLegacyMoistureFormula && (
              <div
                className="ds-series-dock__calib-note"
                style={{
                  marginTop: "8px",
                  paddingTop: "8px",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  fontSize: "12px",
                  lineHeight: 1.45,
                }}
              >
                <span>{t("calibration_species_legacy_settings_hint")} </span>
                <Link to="/settings/customers">{t("calibration_species_settings_link")}</Link>
              </div>
            )}
            {!usesLegacyMoistureFormula && calibrationMappings.length === 0 && (
              <div
                className="ds-series-dock__calib-note"
                style={{
                  marginTop: "8px",
                  paddingTop: "8px",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  fontSize: "12px",
                  lineHeight: 1.45,
                }}
              >
                <span>{t("calibration_species_settings_hint")} </span>
                <Link to="/settings/customers">{t("calibration_species_settings_link")}</Link>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ds-series-dock">
      {renderChip(dragItemLeft, "left")}
      {renderChip(dragItemRight, "right")}
    </div>
  );
}

export default SeriesChips;
